// src/styles/profile/layout.style.ts
import { styled } from '../stitches.config';
import { NAVIGATION_HEIGHT } from '../../constants/global';
import { Button } from '../_app.style';

/* 1 rem margin all around; push one extra rem under the nav */
export const PageShell = styled('div', {
  display: 'flex',
  justifyContent: 'center',

  paddingTop: `calc(${NAVIGATION_HEIGHT} + 1rem)`,
  paddingRight: '1rem',
  paddingBottom: '1rem',
  // paddingLeft: '1rem',

  height: '100dvh',

  // Clip any descendant that paints past the viewport so it doesn't
  // bleed into the global Radix ScrollViewport and scroll the whole page.
  overflow: 'hidden',

  background: 'transparent',

  boxSizing: 'border-box',
});



/* --- The two-pane white card ----------------------------------------- */
/* --- 1. Wrapper: ONLY the grid ------------------------------------ */
export const ProfilePageWrapper = styled('div', {
  display: 'grid',
  gridTemplateColumns: '320px 1fr',
  columnGap: '1rem',          // ← 1-rem “sliver” between panes
  width: '100%',
  height: '100%',             // stretch to fill PageShell
  position: 'relative',
  zIndex: 1,

  '@media (max-width: 81rem)': {
    gridTemplateColumns: '70px 1fr'
  }
});

/* --- 2. Sidebar pane ---------------------------------------------- */
export const SidebarArea = styled('aside', {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.75rem',
  padding: '0.75rem',
  background: '$blueBackground',

  boxShadow: '0 6px 24px rgba(0,0,0,0.04)',
  borderRight: '1px solid $elementBorder',
  borderTop: '1px solid $elementBorder',
  borderBottom: '1px solid $elementBorder',
  borderTopRightRadius: 16,
  borderBottomRightRadius: 16,

  '@media (max-width: 81rem)': {
    padding: '0.75rem 0.25rem', // Reduce side padding
    alignItems: 'center',

    '& span': {
      display: 'none',
    },

    '& button': {
      justifyContent: 'center',
      padding: '0.75rem', 
    }


  }




});

/* --- 3. Content pane ---------------------------------------------- */
export const ContentArea = styled('main', {
  display: 'grid',
  gridTemplateColumns: '450px minmax(0, 1fr)',
  gap: '1rem',
  overflow: 'hidden',
  minWidth: 0,


  variants: {
    hasWarning: {
      true: {
        gridTemplateAreas: `
        "warning warning"
        "left right"
      `,
      gridTemplateRows: 'auto 1fr',
      },
      false: {
        gridTemplateAreas: `
          "left right"
      `,
      gridTemplateRows: '1fr',

      }
    }, 

    isParent: {
      true: {
        gridTemplateColumns: 'minmax(0, 1fr)',
        gridTemplateAreas: `
            'warning'
            'main'
        `,
        gridTemplateRows: '1fr',
      }
    },

  },

  defaultVariants: {
    hasWarning: 'false',
    isParent: 'false',
  },

  compoundVariants: [
    {
      isParent: true,
      hasWarning: true,
      css: {
        gridTemplateColumns: 'minmax(0, 1fr)',
        gridTemplateAreas: `
            'warning'
            'main'
        `,
        gridTemplateRows: 'auto 1fr',

      }
    }, 
    
  ],


  padding: '0',
  background: 'transparent',
  boxShadow: 'none',

  '@media (max-width: 900px)': {
    // 1. Force Single Column
    gridTemplateColumns: 'minmax(0, 1fr)',
    rowGap: '1rem',
    columnGap: '0rem',
    // gap: '0rem',

    // 2. Reset Areas so the Warning Banner doesn't get confused looking for "left/right"
    gridTemplateAreas: `
      "warning"
      "main"
    `,

    // 3. Ensure Warning takes its space, and Content takes the rest
    gridTemplateRows: 'auto 1fr',
  }
});

export const LeftColumnContainer = styled('div', {
  gridArea: 'left', 
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem',
  
  height: '100%', 
  // CRITICAL: minHeight 0 prevents flex child overflow issues
  minHeight: 0, 
  
  // We hide overflow here so the inner cards handle the scrolling
  overflow: 'hidden', 
})

export const RightColumnScrollArea = styled('div', {
  gridArea: 'right', // If you use grid areas
  height: '100%',
  overflow: 'hidden', // Only this part scrolls!
  display: 'flex',
  flexDirection: 'column',
  paddingRight: '4px',
});

export const ParentColumnArea = styled('div', {
  height: '100%',
  overflow: 'hidden', // Only this part scrolls!
  display: 'flex',
  flexDirection: 'column',
  // paddingRight: '4px',
})

const CardBase = {
  background: 'white',
  borderRadius: 16,
  // padding: '1.5rem', // <-- REMOVE THIS LINE
  boxShadow: '0 6px 24px rgba(0,0,0,0.04)',
  
  // Add flex display to help with layout
  display: 'flex',
  flexDirection: 'column',
};

// --- 2. ADD the new CardHeader and CardContent components ---
export const CardHeader = styled('div', {
  padding: '0 1rem',            // Horizontal padding for the title
  height: '3.5rem',               // "a couple of rem long"
  display: 'flex',
  flexShrink: 0,                // CRITICAL: Never let header shrink
  alignItems: 'center',
  justifyContent: 'space-between', // <-- ADD THIS LINE
  borderBottom: '1px solid $elementBorder', // Separator line
  background: '$blueWeeks',
  borderTopLeftRadius: 16,
  borderTopRightRadius: 16,
  // Title text styles
  fontSize: '$md',
  fontWeight: 600,
  color: '$sand12',

  variants: {
    collapsed: {
      true: {
        // When collapsed: Remove border, round the bottom corners
        borderBottom: 'none',
        borderBottomLeftRadius: 16,
        borderBottomRightRadius: 16,
        transition: 'border-radius 0.3s ease', 
      },
      false: {
        // When open: Keep border, square bottom corners
        borderBottom: '1px solid $elementBorder',
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
      }
    }
  }


});

export const HeaderIconButton = styled(Button, {
  all: 'unset', // Removes default button styles
  display: 'grid',
  placeItems: 'center',
  cursor: 'pointer',
  width: '2rem',
  height: '2rem',
  borderRadius: '50%',
  color: '$sand11', // A neutral grey for the icon
  transition: 'background-color 0.2s ease, color 0.2s ease',
    // --- 2. Remove the color change from the hover state ---
    '&:not([data-active="true"]):hover': {
      backgroundColor: '$blueHover',
      // The 'color' property is removed, so it stays black
    '& svg': {
      fill: 'var(--colors-blue11)',
    },  
    },
});

export const CardContent = styled('div', {
  padding: '1.5rem', // The padding that used to be on the card itself
  flexGrow: 1,       // Allows this area to grow and fill available space
  flexShrink: 1,
  minHeight: 0,
  minWidth: 0,
  overflowY: 'auto',
  overflowX: 'hidden',
  // Stop scroll chaining: once the user reaches the end of this card's
  // internal scroll, don't bubble wheel/touch scroll up to the global viewport.
  overscrollBehavior: 'contain',

  fontSize: '$sm',
});

export const ProfilePhotoCard = styled('div', {
  ...CardBase, // Includes background, border-radius, etc.
  gridArea: 'photo', // Includes its own grid position
  
});

export const ProfileDetailsCard = styled('div', {
  ...CardBase,
  gridArea: 'details',
  display: 'flex',
  flexDirection: 'column',
  height: '100%', // or a specific height like `minHeight: '450px'`
});

export const GoogleCard = styled('div', {
  ...CardBase,
  gridArea: 'google',
  display: 'flex',
  flexDirection: 'column',
  height: '100%', // or a specific height like `minHeight: '450px'`
});

export const ProfileDescriptionCard = styled('div', {
  ...CardBase,
  // gridArea: 'description',
  height: '100%', 
  width: '100%'
});

export const ParentProfileCard = styled('div', {
  ...CardBase, // Inherits background, shadow, etc.
  gridColumn: '1 / -1',
  variants: {
    hasWarning: {
      true: {
        // When there's a warning, content starts on row 2
        gridRow: '2 / -1',
      },
      false: {
        // When there's NO warning, content starts on row 1
        gridRow: '1 / -1',
      },
    },
  },

  defaultVariants: {
    hasWarning: false,
  },
});

export const WarningWrapper = styled('div', {
  // gridColumn: '1 / -1',
  gridArea: "warning",
  bottomGap: '1rem',
  // width: '100%',
  // maxWidth: '100%',
  // boxSizing: 'border-box', // Ensures padding doesn't widen it
})

export const CardFooter = styled('div', {
  display: 'flex',
  justifyContent: 'space-between', // Pushes buttons to opposite ends
  alignItems: 'center',
  padding: '1rem 1.5rem', // Match horizontal padding of header
  borderTop: '1px solid $elementBorder', // Separator line
  flexShrink: 0,
  borderBottomLeftRadius: 16,
  borderBottomRightRadius: 16,
  // flexGrow: 1,
  // gap: '8rem', 
});



export const AccordionItemWrapper = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  
  minWidth: 0,
  // Card Appearance
  background: 'white',
  borderRadius: 16,
  boxShadow: '0 6px 24px rgba(0,0,0,0.04)',
  
  // Smoothly animate the growth/shrinking
  // We animate flex-grow to make it slide open/shut
  transition: 'flex-grow 0.3s ease, min-height 0.3s ease',
  
  variants: {
    isOpen: {
      true: {
        // MAGIC: "1" means "Grow to take 1 share of available space"
        flex: '1 1 0%', 
        // CRITICAL: minHeight 0 allows the flex item to shrink smaller than its content
        // This is what forces the inner scrollbar to appear if the screen is too short.
        minHeight: 0,    
        overflow: 'hidden',
      },
      false: {
        // MAGIC: "0" means "Do not grow, just sit there"
        flex: '0 0 auto', 
        // Just high enough to fit the Header
        height: '3.5rem', 
        overflow: 'hidden',
      }
    },

    autoHeight: {
      true: {
        flex: '0 0 auto',
        height: 'auto',
        overflow: 'visible',
      },
    },

    fitContent: {
      true: {
        flex: '0 1 auto', 
        
        height: 'auto',
        minHeight: 0, // Allows shrinking past content size if absolutely necessary
        overflow: 'hidden', // Keeps corners clean
      }
    }
  }, 
  compoundVariants: [
    {
      isOpen: true, 
      autoHeight: true,
      css: {
        flex: '0 0 auto', 
        height: 'auto',
      }
    }, 
    {
      isOpen: true, 
      fitContent: true,
      css: {
        flex: '0 1 auto', 
        minHeight: 0,
        height: 'auto',
      }

    }
  ]
});

export const ChevronToggle = styled('div', {
  display: 'grid',
  placeItems: 'center',
  width: '1.5rem',
  height: '1.5rem',
  borderRadius: '50%', // Circular shape for hover effect
  
  color: '$blue11',    // Adjust to your theme color (e.g. dark blue or gray)
  
  // Smoothly animate the rotation and the background color change
  // transition: 'transform 0.3s ease, background-color 0.2s ease',

  // Subtle hover effect
  // '&:hover': {
  //   backgroundColor: 'rgba(0, 0, 0, 0.05)', 
  // },

  transition: 'background-color 0.2s ease, color 0.2s ease',
    // --- 2. Remove the color change from the hover state ---
    '&:not([data-active="true"]):hover': {
      backgroundColor: '$blueHover',
      // The 'color' property is removed, so it stays black
    '& svg': {
      fill: 'var(--colors-blue11)',
    },  
    },

  variants: {
    collapsed: {
      true: {
        // If collapsed, rotate -90 degrees to point RIGHT
        transform: 'rotate(-90deg)', 
      },
      false: {
        // If open (default for ChevronDown), stay at 0 degrees pointing DOWN
        transform: 'rotate(0deg)', 
      },
    },
  },
});


export const MobileNavRow = styled('div', {
  display: 'none',
  width: '100%',
  paddingBottom: '1rem', 
  gap: '0.5rem', 
  // overflowX: 'auto', 
  flexWrap: 'wrap',

  whiteSpace: 'nowrap',
  '-webkit-overflow-scrolling': 'touch',
  '&::-webkit-scrollbar': { display: 'none' }, // Clean look

  '@media (max-width: 900px)': {
    display: 'flex',
  }

});

export const MobileNavButton = styled('button', {
  flex: '1 0 auto', // Grow, but don't shrink below content
  display: 'flex',
  // gridColumn: '1 / -1',

  alignItems: 'center',
  justifyContent: 'center',
  gap: '0.5rem',
  
  padding: '0.75rem 1rem',
  // background: '$blueBackground',
  background: 'white',
  
  border: '1px solid $elementBorder',
  borderRadius: 99, // Pill shape
  
  fontSize: '$xs',
  fontWeight: 600,
  color: '$slate11',
  boxShadow: '0 2px 5px rgba(0,0,0,0.03)',
  cursor: 'pointer',
  // transition: 'all 0.2s ease',

  // Active state styling could be handled via class or props, 
  // but simple active/hover works for touch
  // '&:active': {
  //   transform: 'scale(0.96)',
  //   background: '$slate2',
  // },

  transition: 'background-color 0.2s ease',
  
    // --- 2. Remove the color change from the hover state ---
    '&:not([data-active="true"]):hover': {
      backgroundColor: '$blueHover',
      '& svg': {
      fill: 'var(--colors-blue11)',
    },  
    '& span': {
      color: 'var(--colors-blue11)', // For the text
    },
  },

  variants: {
    active: {
      true: {
        transform: 'scale(0.96)', 
        backgroundColor: '$blueHover',
        '& svg': {
          fill: 'var(--colors-blue11)',
        },
        '& span': {
          color: 'var(--colors-blue11)', // For the text
        },
    
        // --- 3. Remove the color change from the active state ---
        // The 'color' property is removed, so it stays black
      },
    },
  },
});

// --- 2. LAYOUT VISIBILITY UTILITIES ---

// Wraps the Desktop 2-Column Grid
export const DesktopLayout = styled('div', {
  display: 'contents', // Acts as if it doesn't exist in the DOM tree, preserving Grid
  '@media (max-width: 900px)': {
    display: 'none', // Poof, gone on mobile
  }
});

// Wraps the Mobile Single-Column View
export const MobileLayout = styled('div', {
  display: 'none',
  flexDirection: 'column',
  height: '100%',
  flex: 1,
  minHeight: 0,
  
  // 👇 ADD THESE
  width: '100%',
  maxWidth: '100%',
  boxSizing: 'border-box',
  
  '@media (max-width: 900px)': {
    display: 'flex',
    // gridArea: 'main',
    // gridArea: 'main', // Explicitly snap to the "main" slot defined in ContentArea
  }
});